import {
  ApiError,
  Button,
  ButtonColor,
  disabledQuery,
  FormError,
  InfoPanel,
  InfoPanelColor,
  PageContainer,
  PageContainerSize,
  Panel,
  Title2,
  useDocumentTitle,
  useIsFeatureEnabled,
  useModalConfirmation,
  useSetApiErrors,
  useToastManager,
  useUnsavedChangesModal
} from "@quortex/front-common"
import { useMutation, useSuspenseQuery } from "@tanstack/react-query"
import { useCallback, useEffect, useMemo } from "react"
import { FormProvider, useForm } from "react-hook-form"
import ContentSteeringServerField from "@/App/play/App/Main/Settings/ContentSteering/ContentSteeringServerField"
import {
  type ContentSteeringServerFormType,
  type ContentSteeringServerType,
  getContentSteeringServerWarningModalParams,
  getDefaultValues,
  hasContentSteeringConfigured,
  isNoneChoice,
  shouldShowActionButtons,
  shouldShowPoolChangeWarning,
  shouldShowSwitchConfiguredUrlList
} from "@/App/play/App/Main/Settings/ContentSteering/helpers"
import SwitchConfiguredUrlList from "@/App/play/App/Main/Settings/ContentSteering/SwitchConfiguredUrlList"
import TargetPoolField from "@/App/play/App/Main/Settings/ContentSteering/TargetPoolField"
import ThirdPartyUrlsFields from "@/App/play/App/Main/Settings/ContentSteering/ThirdPartyUrlsFields"
import { longRefetchOptions } from "@/App/play/helpers/input"
import { useCdnList } from "@/queries/playCdns"
import { usePoolList, useUpdatePool } from "@/queries/playPools"

const ContentSteeringServerForm = () => {
  useDocumentTitle("Content steering")
  const { openConfirmationModal } = useModalConfirmation()
  const { data: poolList } = useSuspenseQuery({
    ...usePoolList(),
    ...longRefetchOptions,
    select: data => data.results
  })

  const jitpEnabled = useIsFeatureEnabled("jitp_default_routing")

  const poolListFiltered = useMemo(
    () => (jitpEnabled ? poolList : poolList.filter(pool => pool.type === "premium")),
    [poolList, jitpEnabled]
  )

  const poolWithContentSteering = useMemo(
    () => poolListFiltered.find(pool => pool.contentSteering?.enabled),
    [poolListFiltered]
  )
  const defaultValues = useMemo(() => {
    return getDefaultValues(poolWithContentSteering)
  }, [poolWithContentSteering])

  const form = useForm<ContentSteeringServerFormType>({
    defaultValues,
    mode: "onChange"
  })
  const targetPoolUuid = form.watch("targetPoolUuid")
  const serverChoice = form.watch("server")

  const updatePoolMutation = useMutation(useUpdatePool({ poolUuid: targetPoolUuid }))
  const resetPoolMutation = useMutation(useUpdatePool({ poolUuid: defaultValues?.targetPoolUuid }))
  const cdnListQuery = useCdnList({ poolUuid: defaultValues?.targetPoolUuid })
  const { data: cdnList } = useSuspenseQuery(
    defaultValues?.targetPoolUuid
      ? {
          ...cdnListQuery,
          select: data => data?.results
        }
      : disabledQuery
  )

  useEffect(() => {
    form.reset(defaultValues)
  }, [defaultValues, form])

  const hasOnGoingConfiguration = hasContentSteeringConfigured(defaultValues, cdnList)

  const toastManager = useToastManager()
  const setApiErrors = useSetApiErrors(form)

  const showButtons = shouldShowActionButtons(form.getValues(), form.formState.isDirty)
  const showSwitchUrlList = useMemo(
    () => shouldShowSwitchConfiguredUrlList(defaultValues, targetPoolUuid),
    [defaultValues, targetPoolUuid]
  )
  const showPoolChangeWarning = useMemo(
    () => shouldShowPoolChangeWarning(targetPoolUuid, poolWithContentSteering),
    [targetPoolUuid, poolWithContentSteering]
  )

  const resetFormValues = useCallback(
    (newServerValue: ContentSteeringServerType) => {
      if (newServerValue === defaultValues.server) {
        form.reset(defaultValues, { keepDirty: false })
      } else {
        form.reset(
          {
            dashServerAddress: "",
            hlsServerAddress: "",
            server: newServerValue,
            targetPoolUuid: ""
          },
          { keepDirty: false }
        )
      }
    },
    [defaultValues, form]
  )

  const performServerReset = useCallback(
    async (newServerValue: ContentSteeringServerType) => {
      try {
        await resetPoolMutation.mutateAsync({
          contentSteering: {
            dashServerAddress: "",
            enabled: false,
            hlsServerAddress: "",
            origin: null
          }
        })

        resetFormValues(newServerValue)

        if (isNoneChoice(newServerValue) && hasOnGoingConfiguration) {
          toastManager.success("Content steering disabled")
        }
      } catch (error) {
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
          setApiErrors(error)
        } else {
          toastManager.error({ apiError: error })
        }
        return
      }
    },
    [resetPoolMutation, resetFormValues, hasOnGoingConfiguration, toastManager, setApiErrors]
  )

  const handleServerChange = useCallback(
    (value: ContentSteeringServerType) => {
      if (hasOnGoingConfiguration) {
        const { title, description, buttonLabel } = getContentSteeringServerWarningModalParams(
          isNoneChoice(value),
          defaultValues.server
        )
        openConfirmationModal({
          children: description,
          okBtnLabel: buttonLabel,
          onAccept: () => performServerReset(value),
          title,
          variant: "destructive"
        })
      } else if (poolWithContentSteering) {
        performServerReset(value)
      } else {
        resetFormValues(value)
      }
    },
    [
      hasOnGoingConfiguration,
      defaultValues,
      poolWithContentSteering,
      openConfirmationModal,
      performServerReset,
      resetFormValues
    ]
  )

  const onCancel = () => {
    form.reset(defaultValues)
  }

  const onSubmit = form.handleSubmit(async data => {
    if (!form.formState.isValid) {
      return
    }
    try {
      const updatedPool = await updatePoolMutation.mutateAsync({
        contentSteering: {
          dashServerAddress: data.dashServerAddress,
          enabled: true,
          hlsServerAddress: data.hlsServerAddress,
          origin: isNoneChoice(serverChoice) ? undefined : serverChoice
        }
      })

      toastManager.success(
        <>
          <strong>{serverChoice === "third_party" ? "Third-party" : "Quortex Switch"}</strong>{" "}
          server enabled for <strong>{updatedPool?.name}</strong> pool
        </>
      )
      form.reset({
        dashServerAddress: updatedPool.contentSteering?.dashServerAddress ?? "",
        hlsServerAddress: updatedPool.contentSteering?.hlsServerAddress ?? "",
        server: updatedPool.contentSteering?.origin ?? "",
        targetPoolUuid: data.targetPoolUuid
      })
    } catch (error) {
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        setApiErrors(error, data)
      } else {
        toastManager.error({ apiError: error })
      }
      return
    }
  })

  useUnsavedChangesModal({ form, onSubmit })

  return (
    <PageContainer size={PageContainerSize.L}>
      <form className="flex w-full flex-col" noValidate onSubmit={onSubmit}>
        <FormProvider {...form}>
          <Panel>
            <Title2 className="mb-md">Content steering</Title2>
            <ContentSteeringServerField onChangeServer={handleServerChange} />
            {!!serverChoice && <TargetPoolField poolList={poolListFiltered} />}
            {showSwitchUrlList && (
              <SwitchConfiguredUrlList contentSteering={poolWithContentSteering?.contentSteering} />
            )}
            {serverChoice === "third_party" && <ThirdPartyUrlsFields />}
            {showPoolChangeWarning && (
              <InfoPanel
                className="mt-lg"
                color={InfoPanelColor.DANGER}
                title="Pool change will reset any Quortex Switch configuration"
              >
                <p>
                  Changing to a different pool will reset your existing Switch configuration,
                  including the segmentation tree.
                </p>
              </InfoPanel>
            )}
            <FormError className="mt-md" />
            {showButtons && (
              <div className="mt-lg flex gap-sm">
                <Button color={ButtonColor.SECONDARY} type="button" onClick={onCancel}>
                  Cancel
                </Button>
                <Button
                  color={ButtonColor.PRIMARY}
                  disabled={!form.formState.isValid}
                  type="submit"
                >
                  Save
                </Button>
              </div>
            )}
          </Panel>
        </FormProvider>
      </form>
    </PageContainer>
  )
}

export default ContentSteeringServerForm
